import { useState, useEffect, useCallback, useMemo } from 'react';
import { useApi } from '../../../../hooks/useApi';
import { getProjectRealizationAnalytics } from '../../../../services/ozonRealization';
import { filterBySearch } from '../../../../utils/search';

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────

const REPORT_TABS = [
  { key: 'summary',       label: 'Сводка' },
  { key: 'dynamics',      label: 'Динамика' },
  { key: 'segments',      label: 'Сегменты' },
  { key: 'products',      label: 'Ассортимент' },
  { key: 'first_product', label: 'Первый товар' },
  { key: 'paths',         label: 'Пути' },
];

const TAB_DESCRIPTIONS = {
  summary:       'Итоговые показатели по всем магазинам проекта. Пересечение — покупатели, делавшие заказы в >1 магазинах.',
  dynamics:      'Помесячная динамика по всем кабинетам проекта суммарно.',
  segments:      'Вся база покупателей разбита по числу заказо-дней (1, 2–3, 4–6, 7–10, 11–15, 16+).',
  products:      'По каждому артикулу (offer_id): повторные покупки, кросс-продажи. Агрегировано по всем магазинам проекта.',
  first_product: 'Какой артикул был первым? Куда покупатели шли после него?',
  paths:         'Последовательность артикулов у повторных покупателей по всем магазинам.',
};

// Letter text colors
const ABCD_COLOR   = { A: '#1e8449', B: '#b7770d', C: '#999', D: '#c0392b' };
// Cell background tints
const ABCD_CELL_BG = { A: 'rgba(39,174,96,0.13)', B: 'rgba(243,156,18,0.10)', C: 'transparent', D: 'rgba(231,76,60,0.10)' };

// Standard ABCD (Pareto cumulative):
//   A — items forming top 80% of total value
//   B — next 15% (cumulative 95%)
//   C — all remaining items with value > 0
//   D — items with value = 0 (non-contributing)
function abcdByCumulative(items, getValue) {
  const total = items.reduce((s, r) => s + (getValue(r) || 0), 0);
  if (!total) return () => 'D';
  const sorted = [...items].sort((a, b) => (getValue(b) || 0) - (getValue(a) || 0));
  const map = new Map();
  let cum = 0;
  for (const item of sorted) {
    const v = getValue(item) || 0;
    if (v <= 0) { map.set(item, 'D'); continue; }
    cum += v;
    const share = cum / total;
    map.set(item, share <= 0.80 ? 'A' : share <= 0.95 ? 'B' : 'C');
  }
  return (item) => map.get(item) ?? 'D';
}

const abcdBadge = (letter) =>
  letter
    ? <span style={{ fontWeight: 700, color: ABCD_COLOR[letter] }}>{letter}</span>
    : <span style={{ color: '#ddd' }}>—</span>;

// ─────────────────────────────────────────────────────────────────────────────
// Formatters
// ─────────────────────────────────────────────────────────────────────────────

const pct = (v) => v != null ? `${(v * 100).toFixed(1)}%` : '—';
const rub = (v) => v != null ? Math.round(v).toLocaleString('ru-RU') + ' ₽' : '—';
const num = (v) => v != null ? Number(v).toLocaleString('ru-RU') : '—';

function csvVal(v) {
  if (v == null) return '';
  return `"${String(v).replace(/"/g, '""')}"`;
}

function downloadCsv(cols, rows, filename) {
  const header = cols.map((c) => csvVal(c.label)).join(',');
  const lines = rows.map((r) =>
    cols.map((c) => {
      const raw = c.csvRender ? c.csvRender(r) : (r[c.csvKey || c.key] ?? '');
      return csvVal(raw);
    }).join(',')
  );
  const csv = '﻿' + [header, ...lines].join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─────────────────────────────────────────────────────────────────────────────
// SortableTable
// ─────────────────────────────────────────────────────────────────────────────

function SortableTable({ cols, rows, emptyText = 'Нет данных', filename = 'export.csv', pageSize = null }) {
  const [sortKey, setSortKey] = useState(null);
  const [sortDir, setSortDir] = useState('asc');
  const [page,    setPage]    = useState(1);

  const sorted = useMemo(() => {
    if (!rows?.length) return [];
    if (!sortKey) return rows;
    return [...rows].sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), 'ru');
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [rows, sortKey, sortDir]);

  // reset to page 1 on sort or filter change
  useEffect(() => { setPage(1); }, [sortKey, sortDir, rows]);

  const totalPages = pageSize ? Math.ceil(sorted.length / pageSize) : 1;
  const visible   = pageSize ? sorted.slice((page - 1) * pageSize, page * pageSize) : sorted;

  const toggleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir((d) => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  if (!rows?.length) return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>{emptyText}</p>;

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6, flexWrap: 'wrap', gap: 6 }}>
        {pageSize && totalPages > 1 ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
            <button className="btn btn--sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>‹</button>
            <span style={{ color: 'var(--text-muted)' }}>{page} / {totalPages} ({sorted.length})</span>
            <button className="btn btn--sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>›</button>
          </div>
        ) : <span />}
        <button className="btn btn--sm" onClick={() => downloadCsv(cols, sorted, filename)} style={{ fontSize: 12 }}>↓ CSV</button>
      </div>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr>
              {cols.map((c) => {
                const sk = c.sortKey || c.key;
                const active = sortKey === sk;
                return (
                  <th key={c.key} onClick={() => c.noSort ? null : toggleSort(sk)}
                    style={{ padding: '6px 10px', textAlign: c.right ? 'right' : 'left',
                      background: 'var(--glass-80, #f0f2f5)', whiteSpace: 'nowrap',
                      borderBottom: '1px solid var(--border)', cursor: c.noSort ? 'default' : 'pointer',
                      userSelect: 'none', color: active ? 'var(--primary, #2980b9)' : undefined }}>
                    {c.tooltip
                      ? <span title={c.tooltip} style={{ borderBottom: '1px dotted currentColor', cursor: 'help' }}>{c.label}</span>
                      : c.label}
                    {active ? (sortDir === 'asc' ? ' ↑' : ' ↓') : ''}
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {visible.map((r, i) => (
              <tr key={i} style={{ borderBottom: '1px solid var(--border-light, #eee)' }}>
                {cols.map((c) => (
                  <td key={c.key}
                    style={{ padding: '5px 10px', textAlign: c.right ? 'right' : 'left',
                      whiteSpace: c.wrap ? 'normal' : 'nowrap', maxWidth: c.wrap ? 300 : undefined,
                      ...(c.cellStyle ? c.cellStyle(r) : {}) }}>
                    {c.render ? c.render(r) : r[c.key]}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TabDesc
// ─────────────────────────────────────────────────────────────────────────────

function TabDesc({ tabKey }) {
  const [open, setOpen] = useState(false);
  const text = TAB_DESCRIPTIONS[tabKey];
  if (!text) return null;
  return (
    <div style={{ marginBottom: 12 }}>
      <button type="button"
        style={{ background: 'none', border: 'none', color: 'var(--link, #2980b9)', cursor: 'pointer', fontSize: 12, padding: 0 }}
        onClick={() => setOpen((v) => !v)}>
        {open ? '▲ Скрыть описание' : '▼ Что показывает этот отчёт?'}
      </button>
      {open && <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--text-muted)', maxWidth: 700 }}>{text}</p>}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Report tabs
// ─────────────────────────────────────────────────────────────────────────────

function Metric({ label, value, sub, highlight }) {
  return (
    <div style={{ background: highlight ? 'var(--glass-70, #eaf3fb)' : 'var(--glass-80, #f5f7fa)',
      borderRadius: 8, padding: '12px 16px', minWidth: 160,
      border: highlight ? '1px solid var(--primary-light, #aed6f1)' : 'none' }}>
      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700 }}>{value}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

function SummaryTab({ data }) {
  if (!data) return null;
  const multiStore = data.multi_store_buyers ?? 0;
  const multiStorePct = data.multi_store_buyers_pct ?? 0;
  return (
    <>
      <TabDesc tabKey="summary" />
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
        <Metric label="Покупателей всего"     value={num(data.total_buyers)} />
        <Metric label="Заказо-дней"           value={num(data.total_order_days)} />
        <Metric label="Выручка"               value={rub(data.total_revenue)} />
        <Metric label="Реализация (факт)"     value={rub(data.total_realization)} />
        <Metric label="Ср. чек / заказо-день" value={rub(data.avg_revenue_per_order_day)} />
        <Metric label="Повторники"            value={num(data.repeat_buyers)} sub={pct(data.repeat_buyers_pct) + ' от всех'} />
        <Metric label="Разовые покупатели"    value={num(data.one_time_buyers)} />
      </div>
      <div style={{ marginTop: 16 }}>
        <h4 style={{ margin: '0 0 10px', fontSize: 14 }}>Межмагазинное пересечение</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
          <Metric
            label="Покупатели в >1 магазинах"
            value={num(multiStore)}
            sub={`${pct(multiStorePct)} от всех покупателей`}
            highlight={multiStore > 0}
          />
          <Metric
            label="Только в 1 магазине"
            value={num((data.total_buyers ?? 0) - multiStore)}
            sub={pct(1 - multiStorePct)}
          />
        </div>
        {multiStore === 0 && (
          <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
            Пересечений нет — покупатели не делали заказы в нескольких магазинах проекта одновременно.
          </p>
        )}
      </div>
      <div style={{ marginTop: 16, padding: '12px 16px', background: 'var(--glass-80)', borderRadius: 8, fontSize: 12, color: 'var(--text-muted)', maxWidth: 600 }}>
        <strong>Заказо-день</strong> — уникальная пара «покупатель + дата покупки» по всем магазинам проекта.<br />
        <strong>Пересечение</strong> — покупатель считается пересекающимся, если у него есть покупки хотя бы в двух разных магазинах проекта.
      </div>
    </>
  );
}

function DynamicsTab({ dynamics, daily }) {
  const [view, setView] = useState('monthly');

  const dynCols = [
    { key: 'month',               label: 'Месяц' },
    { key: 'revenue',             label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),   csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка за месяц по всем кабинетам проекта' },
    { key: 'realization',         label: 'Реализация ₽', right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization),
      tooltip: 'Фактическая реализация: выручка за вычетом возвратов и корректировок' },
    { key: 'orders_by_days',      label: 'Заказо-дней',  right: true, render: (r) => num(r.orders_by_days),
      tooltip: 'Заказо-день = уникальная пара «покупатель + дата покупки». Исключает двойной счёт при нескольких товарах в один день' },
    { key: 'customers_total',     label: 'Покупателей',  right: true, render: (r) => num(r.customers_total),
      tooltip: 'Уникальных покупателей, совершивших хотя бы одну покупку в этом месяце' },
    { key: 'new_customers',       label: 'Новых',        right: true, render: (r) => `${num(r.new_customers)} (${pct(r.new_share)})`,       csvRender: (r) => r.new_customers,
      tooltip: 'Покупатели, чья первая покупка в системе произошла в этом месяце. В скобках — % от всех покупателей месяца' },
    { key: 'returning_customers', label: 'Повторных',    right: true, render: (r) => `${num(r.returning_customers)} (${pct(r.returning_share)})`, csvRender: (r) => r.returning_customers,
      tooltip: 'Покупатели с историей покупок в предыдущих месяцах. В скобках — % от всех покупателей месяца' },
    { key: 'a_count', label: 'A (4+)',  right: true, render: (r) => `${r.a_count} (${pct(r.a_share)})`, csvRender: (r) => r.a_count,
      tooltip: 'Активные покупатели: 4 и более уникальных дня покупок в этом месяце' },
    { key: 'b_count', label: 'B (2–3)', right: true, render: (r) => `${r.b_count} (${pct(r.b_share)})`, csvRender: (r) => r.b_count,
      tooltip: 'Регулярные покупатели: 2–3 уникальных дня покупок в этом месяце' },
    { key: 'c_count', label: 'C (1)',   right: true, render: (r) => `${r.c_count} (${pct(r.c_share)})`, csvRender: (r) => r.c_count,
      tooltip: 'Разовые покупатели: ровно 1 день покупок в этом месяце' },
  ];

  const dailyCols = [
    { key: 'date',        label: 'Дата' },
    { key: 'revenue',     label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),     csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка за день по всем кабинетам проекта' },
    { key: 'realization', label: 'Реализация ₽', right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization),
      tooltip: 'Реализация за вычетом возвратов' },
    { key: 'order_days',  label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days),
      tooltip: 'Уникальных пар «покупатель + дата» в этот день' },
    { key: 'buyers',      label: 'Покупателей',  right: true, render: (r) => num(r.buyers),
      tooltip: 'Уникальных покупателей в этот день' },
  ];

  const dynReversed = useMemo(() => [...(dynamics || [])].reverse(), [dynamics]);

  return (
    <>
      <TabDesc tabKey="dynamics" />
      <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
        <button className={`btn btn--sm${view === 'monthly' ? ' btn--primary' : ''}`} onClick={() => setView('monthly')}>По месяцам</button>
        <button className={`btn btn--sm${view === 'daily'   ? ' btn--primary' : ''}`} onClick={() => setView('daily')}>По дням</button>
      </div>
      {view === 'monthly' && <SortableTable cols={dynCols}   rows={dynReversed}  filename="project_dynamics_monthly.csv" />}
      {view === 'daily'   && <SortableTable cols={dailyCols} rows={daily || []}  filename="project_dynamics_daily.csv" />}
    </>
  );
}

function SegmentsTab({ data }) {
  const cols = [
    { key: 'segment',           label: 'Сегмент',
      tooltip: 'Сегмент по числу уникальных дней покупок за весь период: 1, 2–3, 4–6, 7–10, 11–15, 16+' },
    { key: 'customers',         label: 'Покупателей',  right: true, render: (r) => num(r.customers),
      tooltip: 'Число уникальных покупателей в этом сегменте' },
    { key: 'order_days',        label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days),
      tooltip: 'Суммарное число заказо-дней всех покупателей сегмента' },
    { key: 'revenue',           label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),     csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка покупателей этого сегмента' },
    { key: 'revenue_share',     label: '% выручки',    right: true, render: (r) => pct(r.revenue_share),       csvRender: (r) => (r.revenue_share * 100).toFixed(1),
      tooltip: 'Доля выручки сегмента от общей. Лояльные покупатели (16+ дней) обычно формируют непропорционально высокую долю оборота' },
    { key: 'realization',       label: 'Реализация ₽', right: true, render: (r) => rub(r.realization), csvRender: (r) => Math.round(r.realization),
      tooltip: 'Реализация = выручка за вычетом возвратов' },
  ];
  return (
    <>
      <TabDesc tabKey="segments" />
      <SortableTable cols={cols} rows={data} filename="project_segments.csv" />
    </>
  );
}

function ProductsTab({ data }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() =>
    filterBySearch(data, search),
    [data, search]
  );

  const { getRevAbcd, getRepeatAbcd, getSelfAbcd, getCrossAbcd } = useMemo(() => {
    const totalRev = data.reduce((s, r) => s + (r.revenue || 0), 0);
    const relevant = data.filter((r) => totalRev > 0 && (r.revenue || 0) / totalRev >= 0.01);
    const relevSet = new Set(relevant);

    const revFn   = abcdByCumulative(data,     (r) => r.revenue    || 0);
    const repFn   = abcdByCumulative(relevant, (r) => r.repeat_pct || 0);
    const selfFn  = abcdByCumulative(relevant, (r) => r.self_pct   || 0);
    const crossFn = abcdByCumulative(relevant, (r) => r.cross_pct  || 0);

    return {
      getRevAbcd:   (r) => revFn(r),
      getRepeatAbcd:(r) => relevSet.has(r) ? repFn(r)   : null,
      getSelfAbcd:  (r) => relevSet.has(r) ? selfFn(r)  : null,
      getCrossAbcd: (r) => relevSet.has(r) ? crossFn(r) : null,
    };
  }, [data]);

  const cols = [
    { key: '_abcd_rev',   label: 'Выр',  noSort: true, right: true,
      tooltip: 'ABCD по выручке (метод накопленного итога): A — артикулы в топ-80% оборота; B — следующие 15%; C — остаток с выручкой > 0; D — нулевая выручка',
      render: (r) => abcdBadge(getRevAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRevAbcd(r)]    || '' }),
      csvRender: (r) => getRevAbcd(r) },
    { key: '_abcd_rep',   label: 'Пов%', noSort: true, right: true,
      tooltip: 'ABCD по % повторных заказо-дней (накопленный итог): A — артикулы с наибольшим суммарным вкладом в повторные продажи. Только артикулы ≥1% оборота',
      render: (r) => abcdBadge(getRepeatAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRepeatAbcd(r)] || '' }),
      csvRender: (r) => getRepeatAbcd(r) || '' },
    { key: '_abcd_self',  label: 'Себя', noSort: true, right: true,
      tooltip: 'ABCD по самоповторам: доля заказо-дней, где покупатель вернулся за тем же артикулом. A — наибольший суммарный вклад. Только артикулы ≥1% оборота',
      render: (r) => abcdBadge(getSelfAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getSelfAbcd(r)]   || '' }),
      csvRender: (r) => getSelfAbcd(r) || '' },
    { key: '_abcd_cross', label: 'Кросс',noSort: true, right: true,
      tooltip: 'ABCD по кросс-продажам: доля заказо-дней, где покупатель взял другой артикул. A — наибольший суммарный вклад. Только артикулы ≥1% оборота',
      render: (r) => abcdBadge(getCrossAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getCrossAbcd(r)]  || '' }),
      csvRender: (r) => getCrossAbcd(r) || '' },
    { key: 'offer_id',          label: 'Артикул', render: (r) => r.offer_id || r.sku, csvRender: (r) => r.offer_id || r.sku,
      tooltip: 'Артикул продавца (offer_id) — единый идентификатор товара по всем магазинам проекта' },
    { key: 'name',              label: 'Товар', wrap: true, noSort: true,
      tooltip: 'Название товара' },
    { key: 'revenue',           label: 'Выручка ₽',    right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка по всем заказам с этим артикулом',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRevAbcd(r)] || '' }) },
    { key: 'buyers',            label: 'Покупателей',  right: true, render: (r) => num(r.buyers),
      tooltip: 'Число уникальных покупателей, купивших этот артикул хотя бы раз' },
    { key: 'order_days',        label: 'Заказо-дней',  right: true, render: (r) => num(r.order_days),
      tooltip: 'Уникальных пар «покупатель + дата» с этим артикулом. Единица анализа повторных покупок' },
    { key: 'repeat_order_days', label: 'Повторных д.', right: true, render: (r) => num(r.repeat_order_days),
      tooltip: 'Из всех заказо-дней — сколько пришлось на покупателей, уже имевших покупки ранее' },
    { key: 'repeat_pct',        label: '% повторов',   right: true, render: (r) => pct(r.repeat_pct),  csvRender: (r) => (r.repeat_pct * 100).toFixed(1),
      tooltip: 'Доля повторных заказо-дней: repeat_order_days / order_days',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getRepeatAbcd(r)] || '' }) },
    { key: 'self_pct',          label: 'Себя %',       right: true, render: (r) => pct(r.self_pct),    csvRender: (r) => (r.self_pct * 100).toFixed(1),
      tooltip: 'Из повторных заказо-дней — доля, где покупатель купил именно этот артикул снова',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getSelfAbcd(r)]   || '' }) },
    { key: 'cross_pct',         label: 'Кросс %',      right: true, render: (r) => pct(r.cross_pct),   csvRender: (r) => (r.cross_pct * 100).toFixed(1),
      tooltip: 'Из повторных заказо-дней — доля, где покупатель купил другой артикул (кросс-продажа)',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getCrossAbcd(r)]  || '' }) },
  ];

  return (
    <>
      <TabDesc tabKey="products" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Поиск по артикулу / названию..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 300, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} из {data.length}</span>
      </div>
      <SortableTable cols={cols} rows={filtered} filename="project_assortment.csv" pageSize={100} />
    </>
  );
}

function FirstProductTab({ data }) {
  const [search, setSearch] = useState('');
  const filtered = useMemo(() =>
    filterBySearch(data, search),
    [data, search]
  );

  const { getBuyersAbcd, getFpCrossAbcd } = useMemo(() => {
    const totalRev = data.reduce((s, r) => s + (r.revenue || 0), 0);
    const relevant = data.filter((r) => totalRev > 0 && (r.revenue || 0) / totalRev >= 0.01);
    const relevSet = new Set(relevant);

    const buyersFn = abcdByCumulative(data,     (r) => r.buyers    || 0);
    const crossFn  = abcdByCumulative(relevant, (r) => r.cross_pct || 0);

    return {
      getBuyersAbcd: (r) => buyersFn(r),
      getFpCrossAbcd:(r) => relevSet.has(r) ? crossFn(r) : null,
    };
  }, [data]);

  const cols = [
    { key: '_abcd_buyers', label: 'Пок%', noSort: true, right: true,
      tooltip: 'ABCD по числу привлечённых покупателей (накопленный итог): A — первые артикулы, через которые пришло 80% всех покупателей',
      render: (r) => abcdBadge(getBuyersAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getBuyersAbcd(r)]  || '' }),
      csvRender: (r) => getBuyersAbcd(r) || '' },
    { key: '_abcd_cross',  label: 'Кросс',noSort: true, right: true,
      tooltip: 'ABCD по кросс-покупкам после первого артикула: A — артикулы, после которых чаще всего покупают другое. Только артикулы ≥1% оборота',
      render: (r) => abcdBadge(getFpCrossAbcd(r)),
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getFpCrossAbcd(r)] || '' }),
      csvRender: (r) => getFpCrossAbcd(r) || '' },
    { key: 'sku',         label: 'Первый артикул', render: (r) => r.sku || '—', csvRender: (r) => r.sku,
      tooltip: 'Артикул (offer_id), который покупатель приобрёл первым в истории своих заказов' },
    { key: 'name',        label: 'Товар', wrap: true, noSort: true,
      tooltip: 'Название товара' },
    { key: 'buyers',      label: 'Покупателей',    right: true, render: (r) => num(r.buyers),
      tooltip: 'Число покупателей, у которых этот артикул был первой покупкой',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getBuyersAbcd(r)] || '' }) },
    { key: 'order_days',  label: 'Заказо-дней',    right: true, render: (r) => num(r.order_days),
      tooltip: 'Суммарных заказо-дней покупателей из этой группы за весь период' },
    { key: 'revenue',     label: 'Выручка ₽',      right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка от покупателей, чья первая покупка — этот артикул' },
    { key: 'repeat_days', label: 'Повторных д.',   right: true, render: (r) => num(r.repeat_days),
      tooltip: 'Заказо-дней, когда покупатель делал повторную покупку (любого артикула)' },
    { key: 'self_days',   label: 'Снова этот',     right: true, render: (r) => `${num(r.self_days)} (${pct(r.self_pct)})`,   csvRender: (r) => r.self_days,
      tooltip: 'Повторных заказо-дней, где покупатель купил тот же первый артикул. В скобках — % от повторных' },
    { key: 'cross_days',  label: 'Другой артикул', right: true, render: (r) => `${num(r.cross_days)} (${pct(r.cross_pct)})`, csvRender: (r) => r.cross_days,
      tooltip: 'Повторных заказо-дней, где покупатель купил другой артикул (кросс-продажа). В скобках — % от повторных',
      cellStyle: (r) => ({ background: ABCD_CELL_BG[getFpCrossAbcd(r)] || '' }) },
  ];

  return (
    <>
      <TabDesc tabKey="first_product" />
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10, flexWrap: 'wrap' }}>
        <input type="text" placeholder="Поиск по артикулу / названию..."
          value={search} onChange={(e) => setSearch(e.target.value)}
          style={{ padding: '6px 10px', borderRadius: 6, border: '1px solid var(--border)', width: 280, fontSize: 13 }} />
        <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{filtered.length} из {data.length}</span>
      </div>
      <SortableTable cols={cols} rows={filtered} filename="project_first_product.csv" pageSize={100} />
    </>
  );
}

const PATH_CHAR_THRESHOLD = 200;

function PathsTab({ data }) {
  const [expanded, setExpanded] = useState(new Set());

  const toggleExpand = (key) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const cols = [
    { key: 'buyers',      label: 'Покупателей',    right: true, render: (r) => num(r.buyers),
      tooltip: 'Число покупателей, прошедших этот путь по артикулам' },
    { key: 'revenue',     label: 'Выручка ₽',      right: true, render: (r) => rub(r.revenue),    csvRender: (r) => Math.round(r.revenue),
      tooltip: 'Суммарная выручка покупателей с этим путём' },
    { key: 'offer_path',  label: 'Путь (артикулы)', wrap: true, noSort: true,
      tooltip: 'Последовательность артикулов (offer_id) в порядке покупок' },
    {
      key: 'name_path', label: 'Путь (товары)', wrap: true, noSort: true,
      tooltip: 'Последовательность названий товаров. Если путь длинный — нажмите ▼ чтобы раскрыть',
      render: (r) => {
        const text = r.name_path || '—';
        if (text.length <= PATH_CHAR_THRESHOLD) return text;
        const rowKey = r.offer_path || text;
        const isExp = expanded.has(rowKey);
        return (
          <span>
            {isExp ? text : text.slice(0, PATH_CHAR_THRESHOLD) + '…'}
            <button
              type="button"
              onClick={() => toggleExpand(rowKey)}
              title={isExp ? 'Свернуть' : 'Показать полностью'}
              style={{
                marginLeft: 4, background: 'none', border: 'none', padding: '0 2px',
                cursor: 'pointer', color: 'var(--link, #2980b9)', fontSize: 12, verticalAlign: 'middle',
              }}>
              {isExp ? '▲' : '▼'}
            </button>
          </span>
        );
      },
    },
  ];

  return (
    <>
      <TabDesc tabKey="paths" />
      <SortableTable cols={cols} rows={data} emptyText="Нет повторных покупателей с данными." filename="project_paths.csv" pageSize={100} />
    </>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────

export default function OzonRepeatOrdersProject({ projectCabinets }) {
  const { request } = useApi();
  const [loading,       setLoading]       = useState(false);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [error,         setError]         = useState(null);
  const [activeTab,     setActiveTab]     = useState('summary');

  const accountIds = (projectCabinets || []).map((c) => c.id);

  const loadAnalytics = useCallback(async () => {
    if (!accountIds.length) return;
    setLoading(true);
    setError(null);
    try {
      const data = await getProjectRealizationAnalytics({ request, accountIds });
      setAnalyticsData(data);
    } catch (e) {
      setError(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [request, accountIds.join(',')]);

  if (!accountIds.length) {
    return <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>Нет кабинетов в проекте.</p>;
  }

  return (
    <div style={{ padding: '16px 0' }}>
      <div style={{ background: 'var(--glass-80, #f5f7fa)', borderRadius: 8,
        padding: '12px 16px', marginBottom: 16, border: '1px solid var(--border-light, #e8ecf0)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button className="btn btn--primary" onClick={loadAnalytics} disabled={loading}>
            {loading ? 'Загрузка...' : analyticsData ? 'Обновить отчёты' : 'Загрузить отчёты'}
          </button>
          <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
            {accountIds.length} {accountIds.length === 1 ? 'кабинет' : 'кабинета'} в проекте
          </span>
        </div>
        {error && <p style={{ color: '#e74c3c', fontSize: 13, marginTop: 8 }}>Ошибка: {error}</p>}
      </div>

      {loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)', fontSize: 13 }}>
          <span className="spinner" aria-hidden="true" /> Вычисляем агрегированную аналитику…
        </div>
      )}

      {!loading && analyticsData && !analyticsData.has_data && (
        <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
          Нет данных. Сначала выполните синхронизацию в каждом кабинете (раздел Аналитика → Повторные заказы).
        </p>
      )}

      {!loading && analyticsData?.has_data && (
        <>
          <div className="account-tabs account-tabs--secondary" style={{ marginBottom: 16 }}>
            {REPORT_TABS.map((t) => (
              <button key={t.key} type="button"
                className={activeTab === t.key ? 'active' : ''}
                onClick={() => setActiveTab(t.key)}>
                {t.label}
              </button>
            ))}
          </div>

          {activeTab === 'summary'       && <SummaryTab     data={analyticsData.summary} />}
          {activeTab === 'dynamics'      && <DynamicsTab    dynamics={analyticsData.dynamics} daily={analyticsData.daily} />}
          {activeTab === 'segments'      && <SegmentsTab    data={analyticsData.segments} />}
          {activeTab === 'products'      && <ProductsTab    data={analyticsData.products} />}
          {activeTab === 'first_product' && <FirstProductTab data={analyticsData.first_product} />}
          {activeTab === 'paths'         && <PathsTab       data={analyticsData.paths} />}
        </>
      )}
    </div>
  );
}
